'use client';

import { useState, useEffect, useRef } from 'react';
import { Hashtag } from '@/types';
import apiClient from '@/lib/api';

interface HashtagAutocompleteProps {
  selectedHashtags: string[];
  onHashtagsChange: (hashtags: string[]) => void;
  placeholder?: string;
  className?: string;
}

export default function HashtagAutocomplete({
  selectedHashtags,
  onHashtagsChange,
  placeholder = "Add hashtags...",
  className = ""
}: HashtagAutocompleteProps) {
  const [query, setQuery] = useState('');
  const [suggestions, setSuggestions] = useState<Hashtag[]>([]);
  const [isOpen, setIsOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);
  const dropdownRef = useRef<HTMLDivElement>(null);

  // Fetch suggestions when query changes
  useEffect(() => {
    const fetchSuggestions = async () => {
      if (query.length < 2) {
        setSuggestions([]);
        return;
      }

      setLoading(true);
      try {
        const results = await apiClient.searchHashtags(query);
        // Filter out already selected hashtags
        const filteredResults = results.filter(
          hashtag => !selectedHashtags.includes(hashtag.name)
        );
        setSuggestions(filteredResults);
      } catch (error) {
        console.error('Error fetching hashtag suggestions:', error);
        setSuggestions([]);
      } finally {
        setLoading(false);
      }
    };

    const timeoutId = setTimeout(fetchSuggestions, 300);
    return () => clearTimeout(timeoutId);
  }, [query, selectedHashtags]);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        dropdownRef.current &&
        !dropdownRef.current.contains(event.target as Node) &&
        !inputRef.current?.contains(event.target as Node)
      ) {
        setIsOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    setQuery(value);
    setIsOpen(value.length >= 2);
  };

  const handleSelectHashtag = (hashtag: Hashtag) => {
    if (!selectedHashtags.includes(hashtag.name)) {
      onHashtagsChange([...selectedHashtags, hashtag.name]);
    }
    setQuery('');
    setIsOpen(false);
  };

  const handleRemoveHashtag = (hashtagName: string) => {
    onHashtagsChange(selectedHashtags.filter(tag => tag !== hashtagName));
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      if (query.trim()) {
        // Create new hashtag
        const newHashtag = query.trim().startsWith('#') 
          ? query.trim().toLowerCase()
          : '#' + query.trim().toLowerCase();
        
        if (!selectedHashtags.includes(newHashtag)) {
          onHashtagsChange([...selectedHashtags, newHashtag]);
        }
        setQuery('');
        setIsOpen(false);
      }
    } else if (e.key === 'Escape') {
      setIsOpen(false);
      setQuery('');
    }
  };

  return (
    <div className={`relative ${className}`}>
      {/* Selected Hashtags */}
      {selectedHashtags.length > 0 && (
        <div className="flex flex-wrap gap-2 mb-2">
          {selectedHashtags.map((hashtag) => (
            <span
              key={hashtag}
              className="inline-flex items-center px-3 py-1 rounded-full text-sm bg-purple-100 text-purple-800"
            >
              {hashtag}
              <button
                type="button"
                onClick={() => handleRemoveHashtag(hashtag)}
                className="ml-2 text-purple-600 hover:text-purple-800"
              >
                ×
              </button>
            </span>
          ))}
        </div>
      )}

      {/* Input */}
      <input
        ref={inputRef}
        type="text"
        value={query}
        onChange={handleInputChange}
        onKeyDown={handleKeyDown}
        onFocus={() => query.length >= 2 && setIsOpen(true)}
        placeholder={placeholder}
        className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-transparent"
      />

      {/* Dropdown */}
      {isOpen && (
        <div
          ref={dropdownRef}
          className="absolute z-50 w-full mt-1 bg-white border border-gray-300 rounded-md shadow-lg max-h-60 overflow-auto"
        >
          {loading ? (
            <div className="px-3 py-2 text-gray-500 text-sm">Loading...</div>
          ) : suggestions.length > 0 ? (
            <>
              {suggestions.map((hashtag) => (
                <button
                  key={hashtag._id}
                  type="button"
                  onClick={() => handleSelectHashtag(hashtag)}
                  className="w-full text-left px-3 py-2 hover:bg-gray-100 focus:bg-gray-100 focus:outline-none flex items-center justify-between"
                >
                  <span className="font-medium text-gray-900">{hashtag.name}</span>
                  <span className="text-xs text-gray-400">{hashtag.articleCount} articles</span>
                </button>
              ))}
              {query.trim() && !suggestions.some(s => s.name === (query.startsWith('#') ? query : '#' + query)) && (
                <button
                  type="button"
                  onClick={() => {
                    const newHashtag = query.trim().startsWith('#') 
                      ? query.trim().toLowerCase()
                      : '#' + query.trim().toLowerCase();
                    onHashtagsChange([...selectedHashtags, newHashtag]);
                    setQuery('');
                    setIsOpen(false);
                  }}
                  className="w-full text-left px-3 py-2 hover:bg-gray-100 focus:bg-gray-100 focus:outline-none border-t border-gray-200"
                >
                  <span className="text-blue-600">
                    Create &quot;{query.trim().startsWith('#') ? query.trim() : '#' + query.trim()}&quot;
                  </span>
                </button>
              )}
            </>
          ) : query.length >= 2 ? (
            <div className="px-3 py-2 text-gray-500 text-sm">
              No hashtags found. Press Enter to create a new one.
            </div>
          ) : null}
        </div>
      )}
    </div>
  );
}
